import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Camera, CreditCard, Edit2, FileText, Heart,
  ChevronRight, LogOut, MapPin, Phone, ArrowRightLeft,
  Lock, Eye, EyeOff, CheckCircle2, AlertCircle, X,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { profileAPI } from '../../api/profile';
import { authAPI } from '../../api/auth';
import { getStorageUrl } from '../../utils/storage';
import { Card } from '../../components/ui/Card';
import Button from '../../components/ui/Button';

const MENU_ITEMS = [
  { to: '/profile/photo',      Icon: Camera,           label: 'Profile Photo',       sub: 'Upload or change photo' },
  { to: '/profile/edit',       Icon: Edit2,            label: 'Edit Profile',        sub: 'Update address & contact info' },
  { to: '/profile/bank',       Icon: CreditCard,       label: 'Bank Details',        sub: 'Manage payment info' },
  { to: '/profile/nominee',    Icon: Heart,            label: 'Nominee Details',     sub: 'Add/update nominee' },
  { to: '/profile/id-proof',   Icon: FileText,         label: 'ID Proof',            sub: 'Upload documents' },
  { to: '/profile/transfers',  Icon: ArrowRightLeft,   label: 'Transfer Tracking',   sub: 'Track your withdrawal transfers' },
];

// ── Password field with show/hide ─────────────────────────────
function PasswordField({ label, name, value, onChange, error, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="flex flex-col gap-1.5">
      <label className="input-label">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete="off"
          className={`input-field pr-10 ${error ? 'border-red-400' : ''}`}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2"
          tabIndex={-1}
        >
          {show
            ? <EyeOff size={15} style={{ color: 'rgba(255,255,255,0.35)' }} />
            : <Eye    size={15} style={{ color: 'rgba(255,255,255,0.35)' }} />}
        </button>
      </div>
      {error && <p className="text-xs" style={{ color: '#fca5a5' }}>{error}</p>}
    </div>
  );
}

// ── Change Password Bottom Sheet ──────────────────────────────
function ChangePasswordSheet({ onClose }) {
  const [form, setForm] = useState({
    current_password:          '',
    new_password:              '',
    new_password_confirmation: '',
  });
  const [errors,     setErrors]     = useState({});
  const [apiError,   setApiError]   = useState('');
  const [success,    setSuccess]    = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
    if (apiError)     setApiError('');
  };

  const validate = () => {
    const errs = {};
    if (!form.current_password.trim())
      errs.current_password = 'Current password is required';
    if (!form.new_password)
      errs.new_password = 'New password is required';
    else if (form.new_password.length < 6)
      errs.new_password = 'Password must be at least 6 characters';
    if (!form.new_password_confirmation)
      errs.new_password_confirmation = 'Please confirm your new password';
    else if (form.new_password !== form.new_password_confirmation)
      errs.new_password_confirmation = 'Passwords do not match';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    setApiError('');
    try {
      const { data } = await authAPI.changePassword({
        current_password:          form.current_password,
        new_password:              form.new_password,
        new_password_confirmation: form.new_password_confirmation,
      });
      if (data.status) {
        setSuccess(true);
      } else {
        setApiError(data.message || 'Failed to change password.');
      }
    } catch (err) {
      setApiError(
        err?.response?.data?.message ||
        err?.response?.data?.errors?.current_password?.[0] ||
        err?.response?.data?.errors?.new_password?.[0] ||
        'Something went wrong. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={() => !submitting && onClose()}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="w-full max-w-app rounded-t-3xl p-6 pb-10"
        style={{
          background: 'linear-gradient(180deg, #1e3278 0%, #0f1b50 100%)',
          border: '1px solid rgba(212,160,23,0.2)',
          borderBottom: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-5 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-2xl flex-shrink-0"
              style={{ background: 'rgba(212,160,23,0.12)', border: '1px solid rgba(212,160,23,0.2)' }}
            >
              <Lock size={16} style={{ color: '#d4a017' }} />
            </div>
            <div>
              <h3 className="font-serif text-base font-semibold text-white">Change Password</h3>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Update your account password
              </p>
            </div>
          </div>
          {!submitting && (
            <button
              onClick={onClose}
              className="flex h-7 w-7 items-center justify-center rounded-xl"
              style={{ background: 'rgba(255,255,255,0.08)' }}
            >
              <X size={14} className="text-white" />
            </button>
          )}
        </div>

        {/* Success state */}
        {success ? (
          <div className="flex flex-col items-center gap-4 py-4">
            <div
              className="flex h-14 w-14 items-center justify-center rounded-full"
              style={{ background: 'rgba(5,150,105,0.15)', border: '1px solid rgba(5,150,105,0.3)' }}
            >
              <CheckCircle2 size={28} style={{ color: '#6ee7b7' }} />
            </div>
            <p className="text-sm font-semibold text-white">Password changed successfully!</p>
            <button
              onClick={onClose}
              className="w-full rounded-2xl py-3.5 text-sm font-semibold"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)' }}
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
            {/* API error */}
            {apiError && (
              <div
                className="flex items-start gap-2 rounded-xl px-3 py-2.5 text-xs"
                style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}
              >
                <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
                <span>{apiError}</span>
              </div>
            )}

            <PasswordField
              label="Current Password"
              name="current_password"
              value={form.current_password}
              onChange={handleChange}
              error={errors.current_password}
              placeholder="Enter current password"
            />
            <PasswordField
              label="New Password"
              name="new_password"
              value={form.new_password}
              onChange={handleChange}
              error={errors.new_password}
              placeholder="Min. 6 characters"
            />
            <PasswordField
              label="Confirm New Password"
              name="new_password_confirmation"
              value={form.new_password_confirmation}
              onChange={handleChange}
              error={errors.new_password_confirmation}
              placeholder="Re-enter new password"
            />

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="flex-1 rounded-2xl py-3.5 text-sm font-semibold disabled:opacity-50"
                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)' }}
              >
                Cancel
              </button>
              <Button type="submit" variant="gold" loading={submitting} className="flex-1">
                Update Password
              </Button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────
export default function ProfilePage() {
  const { user, logout } = useAuth();
  const [profile,          setProfile]          = useState(null);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    profileAPI
      .getProfile()
      .then((res) => setProfile(res.data.data))
      .catch(() => {});
  }, []);

  const photoUrl = getStorageUrl(profile?.profile_photo);

  return (
    <>
      <div>
        {/* Header */}
        <div className="px-5 pt-14 pb-6">
          <div className="flex items-start gap-4">

            {/* Avatar */}
            <Link to="/profile/photo" className="relative flex-shrink-0">
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt="Profile"
                  className="w-16 h-16 rounded-3xl object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div
                  className="w-16 h-16 rounded-3xl flex items-center justify-center text-2xl font-serif font-bold text-white"
                  style={{ background: 'linear-gradient(135deg, #d4a017, #f0c040)' }}
                >
                  {user?.name?.[0]?.toUpperCase() || 'U'}
                </div>
              )}
              <div
                className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center shadow-sm"
                style={{ background: 'linear-gradient(135deg, #d4a017, #f0c040)' }}
              >
                <Camera size={11} color="white" />
              </div>
            </Link>

            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-serif font-bold text-white">{user?.name}</h1>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>{user?.mobile}</p>

              {profile?.address && (
                <div className="flex items-start gap-1 mt-1.5">
                  <MapPin size={12} color="#f0c040" className="mt-0.5 flex-shrink-0" />
                  <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>{profile.address}</p>
                </div>
              )}

              {profile?.additional_phone && (
                <div className="flex items-center gap-1 mt-1">
                  <Phone size={12} color="#f0c040" className="flex-shrink-0" />
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{profile.additional_phone}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-4 pb-6 flex flex-col gap-3">

          {/* Account section */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider mb-2 px-1" style={{ color: 'rgba(212,160,23,0.7)' }}>
              Account Settings
            </p>
            <Card className="p-0 overflow-hidden" style={{ borderColor: 'rgba(212,160,23,0.22)' }}>
              {MENU_ITEMS.map(({ to, Icon, label, sub }, idx) => (
                <motion.div
                  key={to}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.07 }}
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
                >
                  <Link
                    to={to}
                    className="flex items-center gap-3 px-5 py-4 transition-colors"
                    style={{ color: 'inherit' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(212,160,23,0.08)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(212,160,23,0.15)' }}
                    >
                      <Icon size={18} color="#f0c040" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white">{label}</p>
                      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{sub}</p>
                    </div>
                    <ChevronRight size={16} color="rgba(255,255,255,0.3)" />
                  </Link>
                </motion.div>
              ))}

              {/* Change Password row */}
              <motion.div
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: MENU_ITEMS.length * 0.07 }}
              >
                <button
                  onClick={() => setChangingPassword(true)}
                  className="w-full flex items-center gap-3 px-5 py-4 transition-colors"
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(212,160,23,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(212,160,23,0.15)' }}
                  >
                    <Lock size={18} color="#f0c040" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-semibold text-white">Change Password</p>
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>Update your account password</p>
                  </div>
                  <ChevronRight size={16} color="rgba(255,255,255,0.3)" />
                </button>
              </motion.div>
            </Card>
          </div>

          {/* Logout */}
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 rounded-2xl border px-5 py-4 transition-colors"
            style={{
              background: 'rgba(239,68,68,0.08)',
              borderColor: 'rgba(239,68,68,0.3)',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.15)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(239,68,68,0.15)' }}
            >
              <LogOut size={18} color="#fca5a5" />
            </div>
            <span className="text-sm font-semibold" style={{ color: '#fca5a5' }}>Sign Out</span>
          </button>

          <p className="text-center text-xs mt-2" style={{ color: 'rgba(255,255,255,0.3)' }}>
            Shine Jewellery v1.0.0
          </p>
        </div>
      </div>

      {/* Change Password Bottom Sheet */}
      <AnimatePresence>
        {changingPassword && (
          <ChangePasswordSheet onClose={() => setChangingPassword(false)} />
        )}
      </AnimatePresence>
    </>
  );
}
